import '@silevis/reactgrid/styles.css';

import { Column, HeaderCell, ReactGrid, Row, TextCell } from '@silevis/reactgrid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { parse } from 'papaparse';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ExitIcon } from '@/assets/images';
import { MarveriIcon } from '@/pages/overview/common/MarveriIcon';

interface SpreadsheetViewerProps {
  title: string;
  fileUrl: string;
  onClose: () => void;
  onFallbackToPdf?: () => void;
}

interface SheetData {
  name: string;
  columns: Column[];
  rows: Row[];
}

export const SpreadsheetViewer = ({
  title,
  fileUrl,
  onClose,
  onFallbackToPdf,
}: SpreadsheetViewerProps) => {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const getFileExtension = (url: string) => {
    const pathname = url.split('?')[0];
    return pathname.split('.').pop()?.toLowerCase() || '';
  };

  const parseCsvFile = useCallback((csvText: string): string[][] => {
    if (!csvText.trim()) return [];
    const result = parse(csvText, {
      header: false,
      skipEmptyLines: true,
      delimiter: '', // auto-detect
      quoteChar: '"',
      escapeChar: '"',
    });
    return result.data as string[][];
  }, []);

  const parseExcelFile = useCallback(async (arrayBuffer: ArrayBuffer): Promise<SheetData[]> => {
    const workbook = new Workbook();
    await workbook.xlsx.load(arrayBuffer);

    const sheetsData: SheetData[] = [];

    workbook.worksheets.forEach((worksheet) => {
      const worksheetData: string[][] = [];
      let maxColumns = 0;

      worksheet.eachRow((row) => {
        maxColumns = Math.max(maxColumns, row.cellCount);
      });

      worksheet.eachRow((row) => {
        const rowData: string[] = [];
        for (let i = 1; i <= maxColumns; i++) {
          const cell = row.getCell(i);
          // Ensure cell value is always a string
          let cellValue = '';
          try {
            if (cell.text !== undefined && cell.text !== null) {
              cellValue = typeof cell.text === 'string' ? cell.text : String(cell.text);
            } else if (cell.value !== undefined && cell.value !== null) {
              cellValue = typeof cell.value === 'string' ? cell.value : String(cell.value);
            }
          } catch (error) {
            cellValue = '';
          }
          rowData[i - 1] = cellValue;
        }
        worksheetData.push(rowData);
      });

      if (worksheetData.length > 0) {
        const { columns, rows } = createGridFromData(worksheetData);
        sheetsData.push({ name: worksheet.name, columns, rows });
      }
    });

    return sheetsData;
  }, []);

  const createGridFromData = (data: string[][]): { columns: Column[]; rows: Row[] } => {
    if (data.length === 0) return { columns: [], rows: [] };

    const headerCells = data[0] || [];
    const gridColumns: Column[] = [
      { columnId: 'rowHeader', width: 60 },
      ...headerCells.map((_, idx) => ({ columnId: `col-${idx}`, width: 150 })),
    ];

    const gridRows: Row[] = data.map((rowData, rowIndex) => {
      const rowCells = [
        { type: 'header', text: (rowIndex + 1).toString() } as HeaderCell,
        ...headerCells.map((_, colIdx) => {
          let cellText = rowData[colIdx] || '';
          if (typeof cellText !== 'string') {
            cellText = String(cellText);
          }
          return {
            type: 'text',
            text: cellText,
          } as TextCell;
        }),
      ];
      return { rowId: `row-${rowIndex}`, cells: rowCells };
    });

    return { columns: gridColumns, rows: gridRows };
  };

  const loadSpreadsheetData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(`Failed to fetch file: ${response.statusText}`);

      const fileExtension = getFileExtension(fileUrl);

      if (fileExtension === 'csv') {
        const csvText = await response.text();
        const data = parseCsvFile(csvText);
        if (data.length === 0) {
          throw new Error('CSV file is empty or could not be parsed');
        }
        const { columns, rows } = createGridFromData(data);
        setSheets([{ name: 'Sheet1', columns, rows }]);
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        const arrayBuffer = await response.arrayBuffer();
        const excelSheets = await parseExcelFile(arrayBuffer);
        if (excelSheets.length === 0) {
          throw new Error('Excel file has no readable sheets');
        }
        setSheets(excelSheets);
      } else {
        throw new Error('Unsupported file format');
      }

      setActiveSheetIndex(0);
    } catch (error) {
      setSheets([]);
      // Automatically trigger fallback to PDF
      if (onFallbackToPdf) {
        setTimeout(() => {
          onFallbackToPdf();
        }, 100);
      }
    } finally {
      setLoading(false);
    }
  }, [fileUrl, parseCsvFile, onFallbackToPdf, parseExcelFile]);

  useEffect(() => {
    loadSpreadsheetData();
  }, [loadSpreadsheetData]);

  const handleClose = useCallback(() => onClose(), [onClose]);

  const handleDownload = useCallback(async () => {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    saveAs(blob, title);
  }, [fileUrl, title]);

  const handleFallbackToPdf = useCallback(() => {
    if (onFallbackToPdf) {
      onFallbackToPdf();
    }
  }, [onFallbackToPdf]);

  const SafeReactGrid = useMemo(() => {
    if (!sheets[activeSheetIndex] || sheets[activeSheetIndex].rows.length === 0) {
      return null;
    }

    try {
      // Validate that all cells have proper text properties
      const currentSheet = sheets[activeSheetIndex];
      const validatedRows = currentSheet.rows.map((row) => ({
        ...row,
        cells: row.cells.map((cell) => {
          if (cell.type === 'text' || cell.type === 'header') {
            return {
              ...cell,
              text: typeof cell.text === 'string' ? cell.text : String(cell.text || ''),
            };
          }
          // For other cell types, return as-is without modifying `.text`
          return cell;
        }),
      }));

      return (
        <ReactGrid rows={validatedRows} columns={currentSheet.columns} onCellsChanged={() => {}} />
      );
    } catch (error) {
      setTimeout(() => {
        handleFallbackToPdf();
      }, 100);
      return (
        <div className="bg-gray-600">Error rendering spreadsheet. Switching to PDF viewer...</div>
      );
    }
  }, [sheets, activeSheetIndex, handleFallbackToPdf]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [handleClose]);

  return (
    <div
      className="absolute left-0 top-0 z-50 flex size-full flex-col overflow-hidden bg-[#0c0c0c86]"
      data-testid="spreadsheet-viewer"
    >
      <div className="relative flex h-[70px] w-full items-center justify-center gap-4 border-b border-b-light-border bg-black p-4 text-center text-marveri-white">
        <span className="flex break-words text-left text-[12px]">{title}</span>
        <div className="absolute right-10 top-[30%] flex gap-4">
          <button
            onClick={handleDownload}
            className="rounded bg-gray-600 px-3 py-1 text-xs text-white hover:bg-gray-800"
          >
            Download
          </button>
          <MarveriIcon
            icon={ExitIcon}
            iconStyle="h-[20px] w-[20px]"
            iconType="other"
            onClick={handleClose}
          />
        </div>
      </div>

      <div className="flex size-full flex-col bg-white">
        {sheets.length > 1 && (
          <div className="flex items-center gap-4 border-b bg-white p-2">
            {sheets.map((sheet, index) => (
              <button
                key={sheet.name}
                className={`px-3 py-1 ${
                  activeSheetIndex === index
                    ? 'rounded bg-blue-500 font-bold text-white'
                    : 'rounded bg-gray-600 text-white hover:bg-gray-600'
                }`}
                onClick={() => setActiveSheetIndex(index)}
              >
                {sheet.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-1 items-center justify-center">
          {loading ? (
            <div className="text-gray-600">Loading spreadsheet...</div>
          ) : sheets.length === 0 ? (
            <div className="text-gray-600">No data found in the file</div>
          ) : (
            <div className="size-full overflow-auto p-4">{SafeReactGrid}</div>
          )}
        </div>
      </div>
    </div>
  );
};
